import {
    Autocomplete,
    createFilterOptions,
    FormControl,
    FormHelperText,
    Skeleton,
    Stack,
    TextField,
} from '@mui/material';
import React, { EventHandler, SyntheticEvent, useEffect, useRef } from 'react';
import { CmsContent } from '../../api/types/cms';
import { useTranslation } from '../../contexts/TranslationContext';
import { useFormValueMatcher } from '../../contexts/FormContext';
import { FormValues } from '../form/types';
import { useCmsContentConnect } from '../../contexts/CmsContentConnectContext';

type CmsContentConnectProps = {
    name?: string;
    label?: string;
    help?: string;
    matcher?: { [input: string]: string };
};

const pageProps = ['link', 'page'];

export default function CmsContentConnect({
    name,
    label,
    help,
    matcher,
}: CmsContentConnectProps) {
    const oldValues = useRef<FormValues>();
    const { translate } = useTranslation();

    const formValueMatcher = Object.keys(matcher ?? {});

    const { values, onChange } = useFormValueMatcher(formValueMatcher);

    const { items, loading } = useCmsContentConnect();

    useEffect(() => {
        if (values && !oldValues.current) {
            oldValues.current = values;
        }
    }, []);

    const selectItem = (
        event: SyntheticEvent<HTMLInputElement>,
        selectedItem: CmsContent,
    ) => {
        const newValues: any = {};
        for (const inputMatcher of formValueMatcher) {
            if (pageProps.includes(inputMatcher)) {
                newValues[inputMatcher] = selectedItem?.page ?? null;
            } else {
                const found = selectedItem?.attachments.find(
                    item => item.content_name === matcher?.[inputMatcher],
                );
                newValues[inputMatcher] = found ?? null;
            }
        }

        if (selectedItem) {
            onChange(newValues);
        } else {
            if (oldValues.current) {
                onChange(oldValues.current);
            }
        }
    };

    const filterOptions = createFilterOptions({
        matchFrom: 'any',
        stringify: (option: CmsContent) => option.content_title,
    });

    return (
        <FormControl fullWidth>
            <Stack spacing={0.5}>
                {loading ? (
                    <Skeleton
                        width="100%"
                        animation="wave"
                        sx={{ transform: 'none', height: 42.45 }}
                    />
                ) : (
                    <Autocomplete
                        fullWidth
                        filterOptions={filterOptions}
                        getOptionLabel={option => option.content_title}
                        inputMode="text"
                        size="small"
                        renderInput={params => (
                            <TextField
                                {...params}
                                size="medium"
                                label={translate(label ?? '')}
                                InputLabelProps={{
                                    sx: { color: 'text.secondary', mt: 0.5 },
                                }}
                                variant="outlined"
                            />
                        )}
                        options={items}
                        onChange={selectItem as EventHandler<any>}
                    />
                )}
                {help && (
                    <FormHelperText id={`${name ?? 'autocomplete'}-help`}>
                        {translate(help)}
                    </FormHelperText>
                )}
            </Stack>
        </FormControl>
    );
}
